import { unByKey as olUnByKey } from 'ol/Observable';

import { View } from 'cadenza/map-dashboard/view/view';
import { on, unByKey } from 'cadenza/utils/event-util';
import { MapProperty } from 'cadenza/map/map-property';
import { LayerProperty } from 'cadenza/map/layer-property';
import { MAP, STATE, VIEW } from 'cadenza/map-dashboard/event-types';
import { selectFeaturesById } from 'cadenza/api-client/maps-api';
import { refreshLayer } from 'cadenza/map/layer-utils';
import { context } from 'cadenza/map/context/context';
import { logger } from 'cadenza/utils/logging';

const name = 'd-map-view';

class MapView extends View {

  constructor () {
    super();
    this.classList.add(name);
    this._map = this.querySelector('.map').map;
  }

  connectedCallback () {
    this.dashboard = this.closest('d-map-dashboard');
    this._eventKeys = [
      on(this._map.getTargetElement(), MAP.SELECTOR_LAYER_SELECTION_CHANGED, event => this._onMapFeatureSelectionChanged(event)),
      on(this.dashboard, STATE.SELECTION_CHANGE, event => this._onRowSelectionChanged(event)),
      on(this._map.getTargetElement(), MAP.FILTER_CONDITION_CHANGED, event => this._onFilterConditionChanged(event)),
      on(this.dashboard, VIEW.LAYOUT_CHANGED, () => this._updateMapSize()),
      this._map.getLayers().on('remove', (event) => this._onLayerRemoved(event)),
      on(this, VIEW.TOGGLE_TABLE_VISIBILITY, event => {
        if (event.detail.isVisible) {
          this._registerLayerVisibilityListener(event.detail.viewId);
        }
      })
    ];
    this._olMapEventKeys = [
      this._map.on(`change:${MapProperty.SELECTED_LAYER}`, (event) => this._onMapSelectedLayerChanged(event)),
      this._map.on('moveend', () => this._onMapMoveEnd())
    ];
  }

  _onLayerRemoved (event) {
    const removedLayerId = event.element.get(LayerProperty.ID);
    this.notify(VIEW.TOGGLE_TABLE_VISIBILITY, { viewId: removedLayerId, isVisible: false });
  }

  _updateMapSize () {
    this._map.updateSize();
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
    olUnByKey(this._olMapEventKeys);
    olUnByKey(this._olLayerVisibilityEventKeys);
  }

  _onMapFeatureSelectionChanged (event) {
    event.stopPropagation();
    this.notify(STATE.SELECTION_CHANGE, event.detail);
  }

  _onMapSelectedLayerChanged (event) {
    const oldSelectedLayer = event.oldValue;
    const newSelectedLayer = this._map.get(MapProperty.SELECTED_LAYER);
    if (oldSelectedLayer) {
      this.notify(VIEW.TOGGLE_TABLE_OVERLAY, {
        viewId: oldSelectedLayer.get(LayerProperty.ID),
        isOverlayed: true
      });
    }
    if (newSelectedLayer) {
      this.notify(VIEW.TOGGLE_TABLE_OVERLAY, {
        viewId: newSelectedLayer.get(LayerProperty.ID),
        isOverlayed: false
      });
    }
  }

  _registerLayerVisibilityListener (layerId) {
    const layer = context(this._map).getLayer(layerId);
    olUnByKey(this._olLayerVisibilityEventKeys);
    this._olLayerVisibilityEventKeys = [
      layer.on(`change:${LayerProperty.VISIBLE}`, () => {
        const selectedLayer = this._map.get(MapProperty.SELECTED_LAYER);
        this.notify(VIEW.TOGGLE_TABLE_OVERLAY, {
          viewId: layer.get(LayerProperty.ID),
          isOverlayed: selectedLayer !== layer,
          isDisabled: !layer.getVisible() || !layer.get(LayerProperty.IN_RANGE)
        });
      }),
      layer.on(`change:${LayerProperty.IN_RANGE}`, () => {
        const selectedLayer = this._map.get(MapProperty.SELECTED_LAYER);
        this.notify(VIEW.TOGGLE_TABLE_OVERLAY, {
          viewId: layer.get(LayerProperty.ID),
          isOverlayed: selectedLayer !== layer,
          isDisabled: !layer.getVisible() || !layer.get(LayerProperty.IN_RANGE)
        });
      })
    ];
  }

  _onMapMoveEnd () {
    const view = this._map.getView();
    const mapExtent = view.calculateVisibleExtent();
    const mapSrs = view.getProjection().getCode();
    this.notify(STATE.EXTENT_CHANGE, { mapExtent, mapSrs });
  }

  _onRowSelectionChanged (event) {
    if (!this.isDispatching) {
      const mapSize = this._map.getInnerSize();
      const mapExtent = this._map.getView().calculateVisibleExtent();
      const mapContext = context(this._map);
      const mapScale = mapContext.mapScale;

      Object.keys(event.detail).forEach(layerId => {
        const featureIds = event.detail[layerId];

        selectFeaturesById({
          layerId,
          featureIds,
          mapSize,
          mapExtent,
          mapScale
        }).then(() => {
          refreshLayer(mapContext.getLayer(layerId));
        }).catch(err => logger.error(`Cannot make selection for layer ${layerId}`, err));
      });
    }
  }

  _onFilterConditionChanged (event) {
    event.stopPropagation();
    this.notify(STATE.CONDITION_CHANGE, event.detail);
  }

}

customElements.define(name, MapView);
